import { Box, Button, Typography } from "@mui/material";
import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  LOAD_DISTRIBUTION_TABLE_ACTIONS_TITLE,
  LOAD_DISTRIBUTION_TABLE_BUTTONS,
  LOAD_DISTRIBUTION_TABLE_COLUMNS,
  LOAD_DISTRIBUTION_TABLE_METRICS,
  LOAD_DISTRIBUTION_TABLE_NOTE,
  LOAD_DISTRIBUTION_TABLE_ROWS,
  LOAD_DISTRIBUTION_TABLE_TITLE,
} from "../data";
import { selectLoadDistributionPdfData } from "../../../redux/selector";
import { downloadLoadDistributionPdf } from "./PDF";
import { useStyles } from "./styles";

const resolveCellValue = (value: string, t: (key: string) => string) =>
  value.startsWith("WORK_ORDER.") ? t(value) : value;

const LoadTable = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const pdfData = useSelector(selectLoadDistributionPdfData);

  return (
    <Box className={styles.sectionCard}>
      <Typography className={styles.loadTableTitle}>
        {t(LOAD_DISTRIBUTION_TABLE_TITLE)}
      </Typography>

      <Box className={styles.loadTable}>
        <Box className={styles.loadTableHeadRow}>
          <Typography className={styles.loadTableHeadCell}>
            {t(LOAD_DISTRIBUTION_TABLE_COLUMNS.detail)}
          </Typography>
          <Typography className={styles.loadTableHeadCell}>
            {t(LOAD_DISTRIBUTION_TABLE_COLUMNS.front)}
          </Typography>
          <Typography className={styles.loadTableHeadCell}>
            {t(LOAD_DISTRIBUTION_TABLE_COLUMNS.rear)}
          </Typography>
          <Typography className={styles.loadTableHeadCell}>
            {t(LOAD_DISTRIBUTION_TABLE_COLUMNS.general)}
          </Typography>
        </Box>

        {LOAD_DISTRIBUTION_TABLE_ROWS.map((row) => (
          <Box key={row.labelKey} className={styles.loadTableBodyRow}>
            <Typography className={styles.loadTableBodyCell}>{t(row.labelKey)}</Typography>
            <Typography className={styles.loadTableBodyCell}>
              {resolveCellValue(row.front, t)}
            </Typography>
            <Typography className={styles.loadTableBodyCell}>
              {resolveCellValue(row.rear, t)}
            </Typography>
            <Typography className={styles.loadTableBodyCell}>
              {resolveCellValue(row.general, t)}
            </Typography>
          </Box>
        ))}
      </Box>

      <Typography className={styles.loadTableNote}>{t(LOAD_DISTRIBUTION_TABLE_NOTE)}</Typography>

      <Box className={styles.loadTableMetrics}>
        {LOAD_DISTRIBUTION_TABLE_METRICS.map((metric, index) => (
          <Box
            key={metric.labelKey}
            className={clsx(
              styles.loadTableMetricField,
              index > 0 && styles.loadTableMetricFieldSeparated
            )}
          >
            <Typography component="span" className={styles.loadTableMetricLabel}>
              {t(metric.labelKey)}
            </Typography>
            <Box className={styles.loadTableMetricValueBox}>
              <Typography component="span" className={styles.loadTableMetricValue}>
                {metric.value}
              </Typography>
            </Box>
            {metric.unitKey ? (
              <Typography component="span" className={styles.loadTableMetricUnit}>
                {t(metric.unitKey)}
              </Typography>
            ) : null}
          </Box>
        ))}
      </Box>

      <Box className={styles.loadTableActions}>
        <Typography className={styles.loadTableActionsTitle}>
          {t(LOAD_DISTRIBUTION_TABLE_ACTIONS_TITLE)}
        </Typography>
        <Box className={styles.loadTableButtons}>
          <Button className={styles.loadTableOutlinedButton} variant="outlined">
            {t(LOAD_DISTRIBUTION_TABLE_BUTTONS.reset)}
          </Button>
          <Button className={styles.loadTableOutlinedButton} variant="outlined">
            {t(LOAD_DISTRIBUTION_TABLE_BUTTONS.digitalSignature)}
          </Button>
          <Button className={styles.loadTableOutlinedButton} variant="outlined">
            {t(LOAD_DISTRIBUTION_TABLE_BUTTONS.sendEmail)}
          </Button>
          <Button
            className={styles.loadTablePrimaryButton}
            variant="contained"
            color="primary"
            onClick={() => downloadLoadDistributionPdf(pdfData)}
          >
            {t(LOAD_DISTRIBUTION_TABLE_BUTTONS.print)}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default LoadTable;
